import React, { forwardRef } from 'react';
import styles from './Checkbox.module.css';

/**
 * Checkbox Component
 *
 * A modern checkbox component with custom styling and animations
 * following the AB Civil design system.
 *
 * @param {Object} props
 * @param {string} props.label - Checkbox label
 * @param {boolean} props.checked - Checked state
 * @param {function} props.onChange - Change handler
 * @param {boolean} props.disabled - Disable checkbox
 * @param {string} props.error - Error message
 * @param {string} props.helperText - Helper text
 * @param {string} props.size - Checkbox size: 'sm' | 'md' | 'lg'
 * @param {string} props.className - Additional CSS classes
 */
const Checkbox = forwardRef(
  (
    {
      label = null,
      checked = false,
      onChange,
      disabled = false,
      error = null,
      helperText = null,
      size = 'md',
      className = '',
      ...rest
    },
    ref
  ) => {
    const hasError = !!error;
    const showHelperText = error || helperText;

    const containerClasses = [
      styles.container,
      hasError && styles.hasError,
      disabled && styles.isDisabled,
      className,
    ]
      .filter(Boolean)
      .join(' ');

    const checkboxClasses = [
      styles.checkbox,
      styles[size],
      checked && styles.checked,
      hasError && styles.error,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <div className={containerClasses}>
        <label className={styles.label}>
          <input
            ref={ref}
            type="checkbox"
            className={styles.input}
            checked={checked}
            onChange={onChange}
            disabled={disabled}
            {...rest}
          />
          <span className={checkboxClasses}>
            {checked && (
              <svg
                className={styles.checkIcon}
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M13.3334 4L6.00002 11.3333L2.66669 8"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
          </span>
          {label && <span className={styles.labelText}>{label}</span>}
        </label>

        {showHelperText && (
          <p className={hasError ? styles.errorText : styles.helperText}>
            {error || helperText}
          </p>
        )}
      </div>
    );
  }
);

Checkbox.displayName = 'Checkbox';

export default Checkbox;
